/**
 * The Pilot sign-in role (MB's R156; decisions 917, 918; docs/specs/mobile/teslyn-mobile.md §15). A pilot's Teslyn
 * account is phone only: the tracking map, photos of fuel receipts and MRs, the hazard map, the block boundary KML
 * and the recce map (R157). No Teslyn Field and no Teslyn Cloud pages. Someone who is also an operator (or holds any
 * other staff role) keeps everything that role gives. The database enforces it (Postgres
 * `20261103100100_pilot_role.sql`: `my_tenant_ids()` leaves pilot-only organisations out, project access `pilot`,
 * and the pilot policies on tracking, document_scan, hazard pins and packs, blocks and lines); this module words it
 * and chooses what the phone shows.
 *
 * Licences (decision 918, refining 290): a pilot-only sign-in takes no licence seat, keeps the limit of two phones,
 * is never given a laptop, and is counted apart on the Licences page. Alerts (decisions 53, 811, 870): a pilot gets
 * no Flight started, Flight ended or storm alerts; a pilot-only sign-in is not notice staff at all.
 *
 * A pilot starts a flight (decision 971, 3 Nov 2026): "Start a flight, pilots can start flight which will default to
 * ferry, operator needs to then be able to change flight type later from teslyn field if needed." Teslyn has no
 * flight-type field: a ferry is a flight entry with no flight number whose time is Ferry hours, its route in the
 * comment, as TJR logged it (decision 235; F26048's ferries of 8 and 9 Oct 2026, "Ferry Griffith to Ceduna"). So a
 * pilot's flight is exactly that: a phone flight with no flight number and the comment "Ferry Ceduna to Forrest"
 * (`newPilotFlightEntry`, `isPhoneFerry`); Teslyn Field offers its flight time as Ferry hours (`phoneFerryHours`),
 * and the operator makes it a survey flight by giving it its number and its log, as any entry.
 *
 * Decision 996 (Q879, 3 Nov 2026): both of a pilot's "Start a flight" buttons stay, the Flights tab's and the one on
 * Photos under the fuel receipt, and make the same ferry flight: the route in the comment and the flight time in
 * Ferry hours. One rule for both, `newPilotFlightEntry` (and `editedPilotFlightEntry` for a change), which is
 * `newPhoneFlightEntry`'s row with the ferry comment, the flight time, Ferry equal to it (`withPilotFerry`), no
 * flight number and the pilot stamped as who started it. Teslyn Field's "Put 4.8 h in Ferry hours" stays for a flight
 * that reached it without hours (an earlier build, or "Add flight time" not pressed yet).
 */
import { addIsoDays } from './dates.js';
import {
  EMPTY_PHONE_FLIGHT_FORM,
  editedPhoneFlightEntry,
  ferryComment,
  ferryRoute,
  hoursText,
  isPhoneFerry,
  newPhoneFlightEntry,
  phoneDayLabel,
  phoneFlightFormFromEntry,
  phoneFlightList,
  phoneFlightState,
  typedHours,
  validatePhoneFlight,
  type PhoneDailyEntry,
  type PhoneFlightForm,
} from './mobile.js';
import type { Aircraft, DailyEntry, DocumentScan, Uuid, ValidationIssue } from './model.js';

/** The sign-in role (`membership.role`, tenant_role 'pilot'). */
export const PILOT_ROLE = 'pilot';

/** The staff sign-in roles: each opens Teslyn Field and Teslyn Cloud as it always has. */
export const STAFF_SIGN_IN_ROLES = [
  'tenant_admin',
  'manager',
  'field_crew',
  'office_staff',
  'data_processor',
] as const;

/** Every tenant sign-in role, in the order the tick boxes show them (Pilot last: it gives the least). */
export const TENANT_SIGN_IN_ROLES = [...STAFF_SIGN_IN_ROLES, PILOT_ROLE] as const;
export type TenantSignInRole = (typeof TENANT_SIGN_IN_ROLES)[number];

/** True when any of `roles` is a staff role (anything but Pilot). */
export function holdsStaffRole(roles: readonly string[]): boolean {
  return roles.some((r) => (STAFF_SIGN_IN_ROLES as readonly string[]).includes(r));
}

/** A sign-in that holds Pilot and no staff role in the organisation: phone only (decision 917). */
export function isPilotOnly(roles: readonly string[]): boolean {
  return roles.includes(PILOT_ROLE) && !holdsStaffRole(roles);
}

/** Pilot-only sign-ins take no licence seat (decision 918). */
export const takesLicenceSeat = (roles: readonly string[]): boolean => holdsStaffRole(roles);

/** The words for a pilot-only sign-in at Teslyn Cloud or Teslyn Field, which it does not open. */
export const PILOT_PHONE_ONLY_WORDS =
  'This is a pilot sign-in: it opens Teslyn Mobile on your phone (the tracking map, hazards, the block boundaries and photos of fuel receipts and MRs). Teslyn Cloud and Teslyn Field are for the rest of the crew and the office. Ask your tenant admin if you need more.';

// ------------------------------------------------------------------------------------------------ the phone's tabs

export type MobileTab = 'flights' | 'photos' | 'map' | 'diurnal' | 'alerts';

const ALL_TABS: readonly MobileTab[] = ['flights', 'photos', 'map', 'diurnal', 'alerts'];
/**
 * A pilot-only sign-in: the map first (tracking, hazards, boundaries, recce, the pre-flight brief), then the flights
 * they start (decision 971), then fuel receipt and MR photos.
 */
const PILOT_TABS: readonly MobileTab[] = ['map', 'flights', 'photos'];

/** The tabs of the phone's bottom bar for these roles, in order. */
export function mobileTabs(roles: readonly string[]): readonly MobileTab[] {
  return isPilotOnly(roles) ? PILOT_TABS : ALL_TABS;
}

/** The tab the app opens on. */
export const firstMobileTab = (roles: readonly string[]): MobileTab => mobileTabs(roles)[0]!;

// ------------------------------------------------------------------------------------------------ fuel receipts and MRs

/**
 * What a pilot photographs on the phone (R156): a fuel receipt (for one of the project's flights) and an MR page
 * (front page or daily log, for one aircraft). Each becomes a `document_scan` row with `source = 'phone'`, read and
 * checked later in Teslyn Field or Teslyn Cloud as any other picture (docs/specs/document-reading.md).
 */
export const PILOT_PICTURE_KINDS = ['fuel_receipt', 'mr_front', 'mr_log'] as const;
export type PilotPictureKind = (typeof PILOT_PICTURE_KINDS)[number];

export const PILOT_PICTURE_LABEL: Readonly<Record<PilotPictureKind, string>> = {
  fuel_receipt: 'Fuel receipt',
  mr_front: 'MR front page',
  mr_log: 'MR daily log page',
};

/**
 * Days of flights the receipt list shows at once (today and the two days before); the project's earlier flights wait
 * behind "Earlier flights", so a pilot's everyday list stays short on a long job (MB's R177: every flight the phone
 * holds can take a receipt, a mobilisation ferry days before the survey included).
 */
export const RECEIPT_FLIGHT_DAYS = 3;

/** A flight a receipt can belong to, in words: "Mon 2 Nov · VH-JOZ · Flight 2". */
export interface ReceiptFlightChoice {
  readonly id: Uuid;
  readonly label: string;
  /** What tells the day's flights apart: "Take-off 13:20 · Ferry 4.8 h · Air BP Ceduna". */
  readonly note: string;
  /** Flown today or in the `RECEIPT_FLIGHT_DAYS - 1` days before: listed at once; the rest behind "Earlier flights". */
  readonly recent: boolean;
}

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
/** "Fri 16 Oct": written out, not Intl, so the phone's engine and Node say the same. */
const dayWords = (iso: string): string => {
  const d = new Date(`${iso}T00:00:00Z`);
  return `${WEEKDAYS[d.getUTCDay()]} ${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]}`;
};

/** The hours split in words, "Survey 1.0 h · Comp box 1.0 h · Ferry 0.9 h", the zero parts left out. */
const hoursSplitWords = (e: PhoneDailyEntry): string[] =>
  (
    [
      ['Survey', e.svy_hours],
      ['Comp box', e.cbx_hours],
      ['Cals', e.cal_hours],
      ['Ferry', e.fry_hours],
      ['External', e.ext_hours],
    ] as const
  )
    .filter(([, h]) => h > 0)
    .map(([name, h]) => `${name} ${hoursText(h)}`);

/** What tells a flight from the others of its day: its take-off, what it was (or where it stands), its fuel stop. */
function receiptFlightNote(e: PhoneDailyEntry): string {
  const parts: string[] = [];
  const state = phoneFlightState(e);
  // a ferry started on a phone says so, and its route, while its split is still the pilot's (decisions 971, 996:
  // none, or Ferry equal to the flight time); once the operator has changed the split, the hours say what it was
  if (isPhoneFerry(e) && pilotSplitOk(e)) parts.push(e.op_comment.trim());
  if (e.takeoff_time) parts.push(`Take-off ${e.takeoff_time}`);
  if (state === 'finished') parts.push(...hoursSplitWords(e));
  else if (state === 'landed') parts.push(`${hoursText(e.flight_time_h ?? 0)} flown`);
  else parts.push('Not landed yet');
  const where = e.fuel_location.trim();
  if (where) parts.push(where);
  return parts.join(' · ');
}

/**
 * Every flight a fuel receipt can be added to: exactly the flights the phone's Flights tab lists (`phoneFlightList`:
 * every flight entry the phone holds, synced or started on the phone and still in its outbox, whatever the flight
 * was for: survey, ferry, mobilisation, comp box, cals or test), newest first; `recent` marks those of the last
 * `RECEIPT_FLIGHT_DAYS` days. The receipt list and the Flights tab come from the same entries and the same rule, so a
 * flight the pilot can see is always a flight the receipt can name (R177).
 */
export function receiptFlightChoices(
  entries: readonly PhoneDailyEntry[],
  aircraft: readonly Pick<Aircraft, 'id' | 'registration'>[],
  today: string,
): ReceiptFlightChoice[] {
  const from = new Date(`${today}T00:00:00Z`);
  from.setUTCDate(from.getUTCDate() - (RECEIPT_FLIGHT_DAYS - 1));
  const since = from.toISOString().slice(0, 10);
  const rego = new Map(aircraft.map((a) => [a.id, a.registration]));
  return phoneFlightList(entries).map((e) => ({
    id: e.id,
    label: [
      dayWords(e.date),
      (e.aircraft_id && rego.get(e.aircraft_id)) || 'No aircraft',
      e.flt_num !== null ? `Flight ${e.flt_num}` : 'Flight',
    ].join(' · '),
    note: receiptFlightNote(e),
    recent: e.date >= since && e.date <= today,
  }));
}

// ------------------------------------------------------------------------------------------------ a pilot's flight

/**
 * Days a pilot may start a flight for: today and the six days before. A pilot-only sign-in's phone reads the last 7
 * days' entries (`daily_entry_pilot_select`), and the database takes a pilot's flight only in that window
 * (20261103140000), so a flight started for an earlier day would vanish from the pilot's phone.
 */
export const PILOT_FLIGHT_DAYS = 7;

/** Who may press "Start a flight" on the Photos tab: a pilot-only sign-in on a project it opens as a pilot. */
export function canStartPilotFlight(roles: readonly string[], access: string | null | undefined): boolean {
  return isPilotOnly(roles) && access === 'pilot';
}

/**
 * What a pilot's "Start a flight" holds, as typed: the phone's flight form (decision 996: the Flights tab's form,
 * with its planned time, refuel and oil, is the same flight) plus the day, the flight time and the route. Every box
 * may stay blank but the date (MB, 2 Nov: no minimum fields). The Photos tab's form shows the day, aircraft, take-off,
 * flight time and route only; its other boxes stay blank.
 */
export interface PilotFlightForm extends PhoneFlightForm {
  /** YYYY-MM-DD, one of `pilotFlightDates`. */
  readonly date: string;
  /** After landing, as typed in hours (3.2 or 3:12); '' until then. */
  readonly flight_time: string;
  readonly from: string;
  readonly to: string;
}

export const emptyPilotFlightForm = (today: string): PilotFlightForm => ({
  ...EMPTY_PHONE_FLIGHT_FORM,
  date: today,
  flight_time: '',
  from: '',
  to: '',
});

/** The days offered, newest first: "Today, Tue 3 Nov 2026", "Yesterday, Mon 2 Nov 2026", "Sun 1 Nov 2026", … */
export function pilotFlightDates(today: string): { readonly id: string; readonly label: string }[] {
  return Array.from({ length: PILOT_FLIGHT_DAYS }, (_, i) => {
    const d = addIsoDays(today, -i);
    return { id: d, label: phoneDayLabel(d, today) };
  });
}

export interface PilotFlightContext {
  /** The organisation's aircraft as the phone read them. */
  readonly aircraft: readonly Aircraft[];
  /** The phone's date, YYYY-MM-DD. */
  readonly today: string;
  /** The flight being changed, or absent when starting one: its own aircraft and date stay acceptable. */
  readonly entry?: Pick<DailyEntry, 'aircraft_id' | 'pilot_id' | 'date'> | null;
}

/**
 * The checks of "Start a flight" and "Change this flight": New flight's own (nothing required; a value must be a
 * value; the places at most 60 characters) and the day. A pilot-only sign-in cannot read the staff register, so no
 * pilot is checked (Teslyn Cloud names the pilot).
 */
export function validatePilotFlight(form: PilotFlightForm, ctx: PilotFlightContext): ValidationIssue[] {
  const issues = validatePhoneFlight(form, {
    aircraft: ctx.aircraft,
    people: [],
    entry: ctx.entry ? { aircraft_id: ctx.entry.aircraft_id, pilot_id: ctx.entry.pilot_id } : null,
  });
  const days = pilotFlightDates(ctx.today).map((d) => d.id);
  if (!days.includes(form.date) && form.date !== ctx.entry?.date)
    issues.push({
      field: 'date',
      message: `Choose today or one of the ${PILOT_FLIGHT_DAYS - 1} days before.`,
    });
  return issues;
}

const dayOfYear = (date: string): number => {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  return Math.round((Date.UTC(y, m - 1, d) - Date.UTC(y, 0, 1)) / 86_400_000) + 1;
};

export interface NewPilotFlightInput {
  readonly id: Uuid;
  readonly project_id: Uuid;
  /** The phone's clock, ISO. */
  readonly nowIso: string;
  readonly form: PilotFlightForm;
  readonly aircraft: Aircraft | null;
  /** The project's entries the phone holds (the previous flight's fuel location, R116, as New flight). */
  readonly entries: readonly DailyEntry[];
  /** The pilot's sign-in (the database stamps the same). */
  readonly userId: Uuid;
}

/**
 * The `daily_entry` row of a flight a pilot starts, from either button (decisions 971 and 996): New flight's row
 * (`newPhoneFlightEntry`: hours 0, `entry_origin = 'mobile'`) on the chosen day, never numbered, with the comment that
 * makes it a ferry, "Ferry Ceduna to Forrest" (`ferryComment`), its flight time when typed and that time in Ferry
 * hours (`withPilotFerry`, the split the pilot policies allow: `daily_entry_pilot_split_ok`), stamped as the pilot's.
 * Call it with a form `validatePilotFlight` accepts.
 */
export function newPilotFlightEntry(input: NewPilotFlightInput): PhoneDailyEntry {
  const { form } = input;
  const row = newPhoneFlightEntry({
    id: input.id,
    project_id: input.project_id,
    date: form.date,
    nowIso: input.nowIso,
    form,
    aircraft: input.aircraft,
    entries: input.entries,
  });
  return withPilotFerry({
    ...row,
    flt_num: null,
    flight_time_h: typedHours(form.flight_time),
    op_comment: ferryComment(form.from, form.to),
    started_by: input.userId,
  });
}

/** A pilot's flight as "Change this flight" (Photos) or "Change flight" (Flights) shows it again. */
export function pilotFlightFormFromEntry(e: PhoneDailyEntry, a: Aircraft | null = null): PilotFlightForm {
  const route = ferryRoute(e.op_comment) ?? { from: '', to: '' };
  const form = phoneFlightFormFromEntry(e, a);
  return {
    ...form,
    date: e.date,
    flight_time: form.flight_time ?? '',
    from: route.from,
    to: route.to,
  };
}

/**
 * The flight with the boxes the pilot changed (as `editedPhoneFlightEntry`: only those columns, so the outbox sends
 * only them): New flight's boxes, the day, the route (the ferry comment, written again only when a place changed) and,
 * when the flight time changed, Ferry hours equal to it (decision 996). Never a flight number.
 */
export function editedPilotFlightEntry(input: {
  readonly entry: PhoneDailyEntry;
  readonly before: PilotFlightForm;
  readonly form: PilotFlightForm;
  readonly aircraft: Aircraft | null;
}): PhoneDailyEntry {
  const { entry, before, form } = input;
  let next = editedPhoneFlightEntry({
    entry,
    before: { ...before, flight_number: '' },
    form: { ...form, flight_number: '' },
    aircraft: input.aircraft,
  });
  if (form.date !== before.date) next = { ...next, date: form.date, jday: dayOfYear(form.date) };
  if (form.from.trim() !== before.from.trim() || form.to.trim() !== before.to.trim())
    next = { ...next, op_comment: ferryComment(form.from, form.to) };
  if (form.flight_time.trim() !== before.flight_time.trim()) next = withPilotFerry(next);
  return next;
}

/**
 * Whether this pilot may still change the flight (the database's rule, 20261103140000, which merges 20261103110000's):
 * one they started, started on a phone, with no flight number, its hours none or the ferry default
 * (`pilotMayChangeFlight`: `pilotSplitOk`, so a flight whose time "Add flight time" put into Ferry hours is still the
 * pilot's). Once the operator numbers it or splits its hours, it is the operator's.
 */
export function canPilotChangeFlight(e: PhoneDailyEntry, userId: Uuid | null | undefined): boolean {
  return !!userId && pilotMayChangeFlight(e, userId);
}

export interface PilotPictureDraft {
  readonly kind: PilotPictureKind;
  readonly aircraftId: Uuid | null;
  readonly dailyEntryId: Uuid | null;
}

/** What is missing before the picture can be kept, in words; null when it can. */
export function pilotPictureIssue(d: PilotPictureDraft): string | null {
  if (d.kind === 'fuel_receipt') return d.dailyEntryId ? null : 'Choose the flight the fuel was for.';
  return d.aircraftId ? null : 'Choose the aircraft whose MR this is.';
}

export interface PilotPictureInput extends PilotPictureDraft {
  readonly id: Uuid;
  readonly tenantId: Uuid;
  readonly projectId: Uuid;
  /** `Fuel receipt_F26048_20261102_071512.jpg`: the name the picture is kept by. */
  readonly fileName: string;
  readonly ext: 'jpg' | 'jpeg' | 'png' | 'webp';
  readonly sizeBytes: number;
  readonly sha256: string;
  readonly now: string;
}

/** The `document_scan` row of a picture taken on the phone (written through `sync_apply`). */
export function pilotPictureRow(i: PilotPictureInput): DocumentScan & { readonly tenant_id: Uuid } {
  const issue = pilotPictureIssue(i);
  if (issue) throw new Error(issue);
  return {
    id: i.id,
    tenant_id: i.tenantId,
    project_id: i.projectId,
    kind: i.kind,
    aircraft_id: i.kind === 'fuel_receipt' ? null : i.aircraftId,
    fuel_entry_id: null,
    daily_entry_id: i.kind === 'fuel_receipt' ? i.dailyEntryId : null,
    file_name: i.fileName.slice(0, 255),
    file_ext: i.ext,
    file_path: '',
    size_bytes: i.sizeBytes,
    sha256: i.sha256,
    checked_values: {},
    read_values: null,
    read_by: '',
    read_at: null,
    checked_by_name: '',
    checked_at: null,
    added_by_name: '',
    source: 'phone',
    created_at: i.now,
    updated_at: i.now,
    updated_by: null,
    deleted_at: null,
  };
}

const pad = (n: number): string => String(n).padStart(2, '0');

/** `Fuel receipt_F26048_20261102_071512.jpg`, from the phone's own clock. */
export function pilotPictureName(
  kind: PilotPictureKind,
  projectNumber: string,
  at: Date,
  ext: string,
): string {
  const stamp = `${at.getFullYear()}${pad(at.getMonth() + 1)}${pad(at.getDate())}_${pad(at.getHours())}${pad(at.getMinutes())}${pad(at.getSeconds())}`;
  const project = projectNumber.trim().replace(/[^A-Za-z0-9_-]+/g, '_') || 'project';
  return `${PILOT_PICTURE_LABEL[kind]}_${project}_${stamp}.${ext}`;
}

// ------------------------------------------------------------------------------------------------ a pilot's flight (decision 971)

type Split = Pick<DailyEntry, 'svy_hours' | 'cbx_hours' | 'cal_hours' | 'fry_hours' | 'ext_hours'>;

/**
 * Whether a flight's hours split is still what a pilot may hold: nothing (not landed yet), or only the ferry default
 * (Ferry equal to the flight time, every other part 0). Mirrors `daily_entry_pilot_split_ok` in
 * 20261103110000_safety_brief.sql, which 20261103140000's pilot policies use too.
 */
export function pilotSplitOk(e: Split & Pick<DailyEntry, 'flight_time_h'>): boolean {
  if (e.svy_hours !== 0 || e.cbx_hours !== 0 || e.cal_hours !== 0 || e.ext_hours !== 0) return false;
  if (e.fry_hours === 0) return true;
  return e.flight_time_h != null && Math.abs(e.fry_hours - e.flight_time_h) < 1e-9;
}

/**
 * A pilot's flight with its flight time: the time goes into Ferry (decision 971: a pilot's flight defaults to a ferry
 * flight; the operator moves it to survey, comp box or cals in Teslyn Field's Daily entry if needed). No flight time:
 * Ferry 0.
 */
export function withPilotFerry<E extends DailyEntry>(e: E): E {
  const h = e.flight_time_h ?? null;
  return { ...e, fry_hours: h !== null && h > 0 ? h : 0 };
}

/**
 * Whether this pilot may still change the flight on the phone: a live flight entry they started (`started_by`, stamped
 * by Teslyn Cloud; a row not in Teslyn Cloud yet was started on this phone), whose split is still theirs
 * (`pilotSplitOk`), with no flight number yet. Once the operator changes the split or numbers it in Teslyn Field it is
 * finished there and read only, as for everyone. Mirrors `daily_entry_pilot_update` (20261103110000, 20261103140000).
 */
export function pilotMayChangeFlight(e: DailyEntry, userId: string | null): boolean {
  if (e.deleted_at !== null || e.entry_type !== 'flight' || e.entry_origin !== 'mobile') return false;
  if (e.flt_num !== null) return false; // numbered by the operator: theirs (20261103140000)
  const own = e.started_by === undefined || e.started_by === null ? true : e.started_by === userId;
  return own && pilotSplitOk(e);
}

/** The words of a pilot's flight on their list. */
export function pilotFlightWords(e: Split & Pick<DailyEntry, 'flight_time_h'>): string {
  const split = e.svy_hours + e.cbx_hours + e.cal_hours + e.fry_hours + e.ext_hours;
  if (split === 0) return e.flight_time_h != null && e.flight_time_h > 0 ? 'Landed' : 'Not landed yet';
  if (pilotSplitOk(e))
    return `Ferry flight, ${e.fry_hours.toFixed(1)} h: the operator can change its type in Teslyn Field`;
  return 'Finished in Teslyn Field';
}

/** The note Teslyn Field's Daily entry shows on a flight a pilot started whose time is still in Ferry. */
export function pilotFerryNote(e: Split & Pick<DailyEntry, 'flight_time_h' | 'entry_origin'>): string | null {
  if (e.entry_origin !== 'mobile' || e.fry_hours === 0 || !pilotSplitOk(e)) return null;
  return `Started on Teslyn Mobile: the flight time (${e.fry_hours.toFixed(1)} h) is in Ferry hours, as a pilot's flight is until you change it. Move it to survey, comp box or cals if it was not a ferry flight.`;
}
